// The sign-off password check (GUI Index View 1207 · Sign-off Modal, the
// re-authentication half; src/blank/PATTERNS.md). Aaron 2026-10-09: "Password
// re-entry before a sign-off → Yes: step-up auth".
//
// It is an ACTION that COLLECTS before it writes (BP-20): the dialog takes the
// signer's password, has the IdP check it (src/auth/stepUp.ts), and hands back
// a fresh ID token, never the password. It never switches user: the identity is
// the session's, shown read-only, as legacy only ever checked the signed-in
// user's own password. Someone else signs in as themselves first.
//
// Built on the shared AlertDialog (title, content slot, actions) and the house
// Cancel / Action buttons; nothing here restyles them.
//
// StepUpHost is mounted once at the app root and answers the shared axios
// interceptor (src/api.ts) whenever the API asks for a proof (428), so every
// sign-off route, today's and later ones (Q4), gets the same dialog without its
// own wiring.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { TextField, Typography } from "@mui/material";

import AlertDialog from "./AlertDialog";
import { ActionButton, CancelButton } from "./Buttons";
import {
  StepUpSignInError,
  registerStepUpPrompt,
  signInForProof,
  stepUpIdentity,
} from "../auth/stepUp";

export interface StepUpDialogProps {
  open: boolean;
  /** The signed-in user, shown read-only: the only account the password may be for. */
  identity: { label: string; username: string } | null;
  /** Why it asks again, e.g. the window ran out; blank on a first ask. */
  reason?: string;
  /** A fresh ID token from the IdP's password sign-in. */
  onProof: (token: string) => void;
  onCancel: () => void;
}

const StepUpDialog: React.FC<StepUpDialogProps> = ({ open, identity, reason, onProof, onCancel }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // A fresh ask never shows the last one's password or refusal.
    if (open) {
      setPassword("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    if (!identity || !password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const token = await signInForProof(identity.username, password);
      setPassword("");
      onProof(token);
    } catch (e) {
      setError(e instanceof StepUpSignInError ? e.message : "The password could not be checked. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      title="Confirm it's you"
      // Generic, for every signature that asks (Aaron 2026-10-09, "Generic wording": a task's
      // sign-off and a Log Book entry alike): "Confirm it's you: enter your password to sign".
      description={reason ? `${reason} Enter your password to sign.` : "Enter your password to sign."}
      handleClose={busy ? () => undefined : onCancel}
      content={
        <form
          data-testid="step-up-dialog"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <TextField
            label="Signed in as"
            value={identity?.label ?? ""}
            fullWidth
            margin="dense"
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              // Two fields and the button outside the form: no implicit submit, so Enter is wired here.
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            error={Boolean(error)}
            helperText={error ?? " "}
            fullWidth
            margin="dense"
            disabled={busy || !identity}
          />
          <Typography variant="body2">Not you? Cancel, then sign out from the top bar and sign in as yourself.</Typography>
        </form>
      }
    >
      <CancelButton onClick={onCancel} disabled={busy} />
      <ActionButton onClick={submit} disabled={busy || !password || !identity}>
        Confirm
      </ActionButton>
    </AlertDialog>
  );
};

export default StepUpDialog;

interface Pending {
  reason: string;
  resolve: (token: string | null) => void;
}

/** The one place the interceptor's 428 becomes a dialog. Mount once, inside the signed-in app. */
export const StepUpHost: React.FC = () => {
  const [pending, setPending] = useState<Pending | null>(null);
  const [identity, setIdentity] = useState<{ label: string; username: string } | null>(null);
  const pendingRef = useRef<Pending | null>(null);

  const ask = useCallback(
    (reason: string) =>
      new Promise<string | null>((resolve) => {
        // A second sign-off arriving while one is asking waits on the same answer.
        const prior = pendingRef.current;
        const next: Pending = {
          reason,
          resolve: (token) => {
            prior?.resolve(token);
            resolve(token);
          },
        };
        pendingRef.current = next;
        setPending(next);
        stepUpIdentity().then(setIdentity);
      }),
    []
  );

  useEffect(() => {
    registerStepUpPrompt(ask);
    return () => registerStepUpPrompt(null);
  }, [ask]);

  const settle = (token: string | null) => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    p?.resolve(token);
  };

  return (
    <StepUpDialog
      open={Boolean(pending)}
      identity={identity}
      reason={pending?.reason}
      onProof={(token) => settle(token)}
      onCancel={() => settle(null)}
    />
  );
};
